import { BracketBadge } from '@/components/BracketBadge';
import { AnalysisText } from '@/components/AnalysisText';
import { CardName } from '@/components/CardTerms';
import { ColorIdentityPips } from '@/components/ColorIdentityPips';
import { ManaCurveSparkline } from '@/components/ManaCurveSparkline';
import { parseDeckContext, type Deck, type PowerLevelResult, type SynergyTheme } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/buildRoles';
import { NEW_CARDS_COPY } from '@/lib/rankingTerms';
import { humanizeLabel, normalizeColorIdentity, scryfallImageUri } from '@/lib/utils';
import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getColorArray(deck: Deck): string[] {
	return normalizeColorIdentity(deck.color_identity);
}

function parseBannerPosition(raw?: string): { x: number; y: number } {
	if (!raw) return { x: 50, y: 25 };
	try {
		const p = JSON.parse(raw);
		return { x: Number(p.x) || 50, y: Number(p.y) || 25 };
	} catch { return { x: 50, y: 25 }; }
}

function getBracket(deck: Deck): number | null {
	// The shown bracket (the rule floor, or the owner's declaration) — never the builder's target.
	return deck.latest_analysis?.power_level?.wotc_bracket ?? null;
}

function getPowerLevel(deck: Deck): PowerLevelResult | null {
	const la = deck.latest_analysis;
	if (!la) return null;
	return la.power_level ?? null;
}

// ─── What's New badge ────────────────────────────────────────────────────────

/** The owner's What's New line under a deck tile: a chip that expands to the picks, or the teaser sentence. */
function NewCardsBadge({ deck }: { deck: Deck }) {
	const [open, setOpen] = useState(false);
	const summary = deck.new_cards_summary;
	if (!summary) return null;
	if (summary.mode === 'teaser') {
		return summary.message
			? <div className="text-sm text-on-surface-variant mt-spacing-sm [&_p]:m-0"><AnalysisText text={summary.message} /></div>
			: null;
	}
	if (summary.mode !== 'judged' || summary.picks.length === 0) return null;
	return (
		<div className="mt-spacing-sm">
			<button
				type="button"
				aria-expanded={open}
				onClick={() => setOpen(o => !o)}
				className="chip cursor-pointer border border-outline-variant bg-[var(--surface-container-high)] text-on-surface"
			>
				{NEW_CARDS_COPY.badge(summary.picks.length)}
			</button>
			{open && (
				<div className="mt-spacing-sm rounded-md p-spacing-sm bg-[var(--surface-container)] border border-outline-variant">
					<ul className="list-none m-0 p-0 flex flex-col gap-spacing-sm">
						{summary.picks.map(p => (
							<li key={p.card} className="text-sm">
								<div className="flex flex-wrap items-center gap-1">
									<CardName name={p.card} className="font-semibold text-on-surface" />
									{p.unreleased && (
										<span className="text-xxs px-1 py-[1px] rounded-[3px] bg-[var(--surface-container-high)] text-on-surface-variant leading-none">{NEW_CARDS_COPY.unreleased}</span>
									)}
									<span className="text-xxs px-1 py-[1px] rounded-[3px] bg-[var(--surface-container-high)] text-on-surface-variant leading-none">{ROLE_LABELS[p.role] ?? p.role}</span>
								</div>
								<div className="text-on-surface-variant [&_p]:m-0"><AnalysisText text={p.reason} /></div>
							</li>
						))}
					</ul>
					<Link to={`/deck/${deck.id}?tab=new-cards`} className="text-sm text-[var(--primary)] mt-spacing-sm inline-block">
						{NEW_CARDS_COPY.openTab}
					</Link>
				</div>
			)}
		</div>
	);
}

// ─── Component ───────────────────────────────────────────────────────────────

interface DeckCardProps {
	deck: Deck;
	isOwner?: boolean;
	onDelete?: (id: string) => void;
}

function DeckCardInner({ deck, isOwner, onDelete }: DeckCardProps) {
	const { hasFeature } = useAuth();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const commanders = deck.commanders ?? [];
	const firstCommander = commanders[0] || null;
	const sid = firstCommander ? deck.cards?.[firstCommander]?.scryfall_id : undefined;
	const artUrl = firstCommander
		? scryfallImageUri(firstCommander, sid, 'art_crop')
		: null;
	const hasArt = !!artUrl;
	const bannerPos = parseBannerPosition(deck.banner_position);
	const colors = getColorArray(deck);
	const bracket = getBracket(deck);
	const pl = getPowerLevel(deck);

	const speedCategory = pl?.speed_category ?? null;
	// The deck's themes as the server computed them (chosen once seeded, else discovered).
	const themes: SynergyTheme[] = deck.themes ?? [];

	const manaCurve: number[] | null = (() => {
		const mc = pl?.mana_curve;
		if (!mc) return null;
		const dist = Array.from({ length: 8 }, (_, i) => mc[String(i)] ?? 0);
		return dist.some(v => v > 0) ? dist : null;
	})();

	const isVariant = deck.source_type === 'variant_materialized';
	const variantParentId: string | null = (() => {
		if (!isVariant) return null;
		const ctx = parseDeckContext(deck);
		const id = ctx?.materialized_from;
		return typeof id === 'string' ? id : null;
	})();

	return (
		<div className="relative group transition-transform duration-500 ease-out hover:scale-[1.06] hover:z-10">
			<Link
				to={`/deck/${deck.id}`}
				className="deck-card-link block relative aspect-[5/7] rounded-md overflow-hidden no-underline border border-outline-variant"
				style={{
					background: hasArt ? 'var(--surface)' : 'linear-gradient(135deg, var(--surface-container, #1e1e1e), var(--surface-container-high, #2a2a2a))',
				}}
			>
				{/* Background art layer — scales on hover to reveal more of the image */}
				{hasArt && (
					<div
						className="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-110"
						style={{
							backgroundImage: `url(${artUrl})`,
							backgroundSize: 'cover',
							backgroundPosition: `${bannerPos.x}% ${bannerPos.y}%`,
						}}
					/>
				)}

				{/* Dark gradient overlay for text readability */}
				<div
					className="absolute inset-0 rounded-[inherit]"
					style={{
						background: hasArt
							? 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.60) 50%, rgba(0,0,0,0.08) 75%, transparent 100%)'
							: 'none',
					}}
				/>

				{/* Card content */}
				<div className="relative z-[1] p-spacing-md flex flex-col justify-end h-full">
					{/* Format badge */}
					{deck.format && (
						<div className="mb-spacing-sm">
							<span className="inline-block px-[10px] py-[2px] rounded-pill bg-white/15 backdrop-blur-[4px] text-on-surface text-xs font-semibold capitalize">
								{deck.format}
							</span>
						</div>
					)}

					{/* Variant origin chip */}
					{isVariant && (
						<div className="mb-spacing-sm">
							{variantParentId ? (
								<Link
									to={`/deck/${variantParentId}`}
									onClick={e => e.stopPropagation()}
									className="inline-flex items-center gap-1 px-[8px] py-[2px] rounded-pill text-xs font-semibold no-underline"
									style={{
										background: 'var(--surface-container-highest)',
										color: 'var(--on-surface-variant)',
										border: '1px solid var(--outline-variant)',
									}}
									title="View origin deck"
								>
									⌖ variant
								</Link>
							) : (
								<span
									className="inline-flex items-center gap-1 px-[8px] py-[2px] rounded-pill text-xs font-semibold"
									style={{
										background: 'var(--surface-container-highest)',
										color: 'var(--on-surface-variant)',
										border: '1px solid var(--outline-variant)',
									}}
								>
									⌖ variant
								</span>
							)}
						</div>
					)}

					{/* Deck name */}
					<div
						className="font-bold text-lg text-on-surface mb-[2px] leading-[1.3]"
						style={{ textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}
					>
						{deck.name}
					</div>

					{/* Commander name */}
					{commanders.length > 0 && (
						<div
							className="text-base text-white/85 mb-spacing-sm"
							style={{ textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}
						>
							{commanders.join(' + ')}
						</div>
					)}

					{/* Color identity mana pips */}
					{colors.length > 0 && (
						<div className="mb-spacing-sm">
							<ColorIdentityPips colors={colors} size="sm" />
						</div>
					)}

					{/* Power info chips (themes show even before a power analysis) */}
					{(pl || themes.length > 0) && (
						<div className="flex flex-wrap gap-1.5 mb-1.5">
							{bracket != null && (
								<BracketBadge
									bracket={bracket}
									variant="chip"
									powerLevel={pl}
								/>
							)}
							{speedCategory && (
								<span className="chip bg-white/15">
									{humanizeLabel(speedCategory)}
								</span>
							)}
							{themes.map(t => (
								<span key={t.slug} className="chip bg-white/10 border border-white/20 text-white/80">
									{t.value}
								</span>
							))}
						</div>
					)}

					{/* Win rate badge (elo flag) */}
					{hasFeature('elo') && deck.game_stats && deck.game_stats.total > 0 && (() => {
						const winPct = Math.round(deck.game_stats.wins / deck.game_stats.total * 100);
						return (
							<div className="flex gap-1.5 mb-1.5">
								<span className="chip bg-white/15">
									{winPct}% wins · {deck.game_stats.total}g
								</span>
							</div>
						);
					})()}

					{/* Mana curve sparkline */}
					{manaCurve && (
						<div className="mb-1 opacity-70">
							<ManaCurveSparkline curve={manaCurve} width={100} height={24} />
						</div>
					)}

					{/* Footer: card count + owner */}
					<div
						className="flex items-center gap-spacing-sm text-sm text-white/70"
						style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
					>
						{deck.card_count != null && <span>{deck.card_count} cards</span>}
						{deck.display_name && (
							<span>· by{' '}
								{(deck.owner_id || deck.user_id) ? (
									<Link
										to={`/user/${deck.owner_id || deck.user_id}`}
										onClick={(e) => e.stopPropagation()}
										className="text-inherit underline underline-offset-2"
									>
										{deck.display_name}
									</Link>
								) : (
									deck.display_name
								)}
							</span>
						)}
					</div>
				</div>
			</Link>

			{/* What's New (owner only): outside the tile link so the chip and picks do not navigate */}
			{isOwner && <NewCardsBadge deck={deck} />}

			{/* Delete button overlay */}
			{isOwner && onDelete && (
				<button
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setShowDeleteConfirm(true);
					}}
					title="Delete deck"
					className="deck-card-delete absolute top-spacing-sm right-spacing-sm z-[2] w-7 h-7 rounded-full border-none bg-black/60 text-on-surface cursor-pointer flex items-center justify-center text-lg leading-none"
				>
					×
				</button>
			)}

			{/* Inline delete confirmation overlay */}
			{showDeleteConfirm ? (
				<div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-spacing-sm rounded-md z-[5]">
					<span className="text-error-text font-semibold text-md">Delete this deck?</span>
					<div className="flex gap-spacing-sm">
						<button
							onClick={() => { onDelete?.(deck.id); setShowDeleteConfirm(false); }}
							className="bg-error-text text-on-surface border-none px-4 py-1.5 rounded-sm cursor-pointer text-base"
						>
							Delete
						</button>
						<button
							onClick={() => setShowDeleteConfirm(false)}
							className="bg-surface-container-high text-on-surface border-none px-4 py-1.5 rounded-sm cursor-pointer text-base"
						>
							Cancel
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}

export const DeckCard = memo(DeckCardInner);
